import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Alert, AlertDescription } from './components/ui/alert';
import { 
  Download, 
  Upload, 
  LogOut, 
  User, 
  Building2, 
  Eye,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import { BACKEND_URL } from "./config";

const AMCDashboard = () => {
  const navigate = useNavigate();
  const [amcName, setAmcName] = useState('');
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showSchemes, setShowSchemes] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [aifCsvFile, setAifCsvFile] = useState(null);
  const [aifUploadLoading, setAifUploadLoading] = useState(false);
  const [availableAmcs, setAvailableAmcs] = useState([]);
  const [showAmcSelector, setShowAmcSelector] = useState(false);
  const [showSchemesModal, setShowSchemesModal] = useState(false);

  useEffect(() => {
    // Check if AMC is logged in
    const isLoggedIn = localStorage.getItem('isAmcLoggedIn') === 'true';
    const storedAmcName = localStorage.getItem('amcName');
    
    if (!isLoggedIn || !storedAmcName) {
      toast.error('Please login to access the dashboard');
      navigate('/login?type=amc');
      return;
    }
    
    // Set AMC name and fetch schemes
    setAmcName(storedAmcName);
    fetchSchemes(storedAmcName);
    
    // Still fetch available AMCs for other purposes
    fetchAvailableAmcs();
  }, [navigate]);

  // ESC key handler for modals
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showSchemesModal) {
          setShowSchemesModal(false);
        }
        if (showAmcSelector) {
          setShowAmcSelector(false);
        }
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showSchemesModal, showAmcSelector]);

  const fetchAvailableAmcs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/available-amcs`);
      const result = await response.json();
      if (result.amcs) {
        setAvailableAmcs(result.amcs);
      }
    } catch (error) {
      console.error('Error fetching AMCs:', error);
    }
  };

  const handleSelectAmc = (selectedAmc) => {
    setAmcName(selectedAmc);
    localStorage.setItem('amcUser', selectedAmc);
    setShowAmcSelector(false);
    fetchSchemes(selectedAmc);
  };

  const fetchSchemes = async (amc) => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/amc-schemes?amc_name=${encodeURIComponent(amc)}`);
      const result = await response.json();
      if (result.schemes) {
        setSchemes(result.schemes);
      }
    } catch (error) {
      console.error('Error fetching schemes:', error);
      toast.error('Failed to fetch schemes');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSampleCSV = async () => {
    try {
      const url = `${BACKEND_URL}/api/download-amc-sample-csv?amc_name=${encodeURIComponent(amcName)}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `${amcName}_sample_template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Sample CSV downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download sample CSV');
    }
  };

  const handleDownloadAIFSampleCSV = async () => {
    try {
      const url = `${BACKEND_URL}/api/aif-sample-csv`;
      const link = document.createElement('a');
      link.href = url;
      link.download = 'aif_sample_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('AIF Sample CSV downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download AIF sample CSV');
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('amc_name', amcName);

    try {
      const response = await fetch(`${BACKEND_URL}/api/upload-amc-data`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        toast.success(`Successfully uploaded data for ${result.updated_count} scheme(s)!`);
        fetchSchemes(amcName); // Refresh schemes
        setCsvFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error(result.message || 'Failed to upload data');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload CSV file');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleAIFFileUpload = async (e) => {
    e.preventDefault();
    if (!aifCsvFile) {
      toast.error('Please select an AIF CSV file');
      return;
    }

    setAifUploadLoading(true);
    const formData = new FormData();
    formData.append('file', aifCsvFile);
    formData.append('amc_name', amcName);

    try {
      const response = await fetch(`${BACKEND_URL}/api/upload-aif-data`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        toast.success(`Successfully uploaded ${result.updated_count} AIF scheme(s)!`);
        fetchSchemes(amcName); // Refresh schemes
        setAifCsvFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"][data-aif="true"]');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error(result.message || 'Failed to upload AIF data');
      }
    } catch (error) {
      console.error('AIF Upload error:', error);
      toast.error('Failed to upload AIF CSV file');
    } finally {
      setAifUploadLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('amcToken');
      if (token) {
        // Call logout endpoint
        await fetch(`${BACKEND_URL}/api/amc-logout?token=${token}`, {
          method: 'POST'
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear localStorage
    localStorage.removeItem('isAmcLoggedIn');
    localStorage.removeItem('amcToken');
    localStorage.removeItem('amcName');
    localStorage.removeItem('amcEmail');
    
    toast.success('Logged out successfully');
    navigate('/login?type=amc');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* AMC Selector Modal - Hidden since AMC is determined by login */}
        {false && showAmcSelector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">Select Your AMC</h2>
              <p className="text-slate-600 mb-6">Choose your AMC from the list below to access the dashboard</p>
              
              <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
                {availableAmcs.map((amc, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectAmc(amc)}
                    className="w-full text-left px-4 py-3 hover:bg-teal-50 border-b border-slate-100 last:border-b-0 transition-colors"
                  >
                    <div className="font-medium text-slate-800">{amc}</div>
                  </button>
                ))}
              </div>
              
              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-slate-600 hover:text-slate-800"
                >
                  ← Back to Home
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/pmskart-logo.png" alt="PMS Kart Logo" className="h-16 w-auto" />
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3">
                <Building2 className="w-10 h-10 text-teal-600" />
                {amcName}
              </h1>
              <p className="text-slate-600 text-lg">
                Manage your scheme information and performance data
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex items-center gap-2 border-red-300 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Welcome Card */}
        <Card className="mb-6 border-teal-200 bg-gradient-to-r from-teal-50 to-blue-50">
          <CardHeader>
            <CardTitle>Welcome to Your AMC Portal</CardTitle>
            <CardDescription>
              Manage your schemes, upload performance data, and keep your information up-to-date
            </CardDescription>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Download Sample CSV */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-teal-600" />
                Download Sample CSV
              </CardTitle>
              <CardDescription>
                Get a pre-filled template with your scheme names and tooltips
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Alert>
                  <FileText className="w-4 h-4" />
                  <AlertDescription>
                    The CSV template includes all your scheme names and helpful tooltips for each field
                  </AlertDescription>
                </Alert>
                
                <Button
                  onClick={handleDownloadSampleCSV}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Sample CSV Template
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Upload CSV */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                Upload Scheme Data
              </CardTitle>
              <CardDescription>
                Upload your completed CSV file to update scheme information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleFileUpload} className="space-y-4">
                <div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={!csvFile || uploadLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {uploadLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload CSV File
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* AIF Data Upload Section */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-600" />
            Upload AIF Data
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Download AIF Sample Template */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-green-600" />
                  Download AIF Sample Template
                </CardTitle>
                <CardDescription>
                  Download a sample CSV template for AIF (Alternative Investment Fund) data with performance columns
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Alert className="bg-indigo-50 border-indigo-200">
                    <AlertCircle className="w-4 h-4 text-indigo-600" />
                    <AlertDescription className="text-indigo-900">
                      AIF template includes: 1M, 3M, 6M, 1Yr, 2Yr, 3Yr, 5Yr, SI performance columns
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={handleDownloadAIFSampleCSV}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download AIF Sample CSV Template
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Upload AIF CSV */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-indigo-600" />
                  Upload AIF Data
                </CardTitle>
                <CardDescription>
                  Upload your completed AIF CSV file
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAIFFileUpload} className="space-y-4">
                  <div>
                    <input
                      type="file"
                      accept=".csv"
                      data-aif="true"
                      onChange={(e) => setAifCsvFile(e.target.files[0])}
                      className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={!aifCsvFile || aifUploadLoading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    {aifUploadLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading AIF Data...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload AIF CSV File
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* View Schemes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              Your Schemes ({schemes.length})
            </CardTitle>
            <CardDescription>
              View all your schemes with detailed information
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                <p className="text-slate-600 mt-2">Loading schemes...</p>
              </div>
            )}

            {!loading && schemes.length === 0 && (
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  No schemes found. Please upload your scheme data using the CSV template above.
                </AlertDescription>
              </Alert>
            )}

            {!loading && schemes.length > 0 && (
              <Button
                onClick={() => setShowSchemesModal(true)}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Schemes
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Schemes Modal */}
        {showSchemesModal && schemes.length > 0 && (() => {
          // Prepare data for grouped bar chart
          const periods = ['1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', 'SI'];
          const periodKeys = ['1_month', '3_month', '6_month', '1_year', '2_year', '3_year', '5_year', 'since_inception'];
          const chartData = periods.map((period, idx) => {
            const dataPoint = { period };
            schemes.forEach(scheme => {
              dataPoint[scheme.scheme_name] = parseFloat(scheme[periodKeys[idx]]) || 0;
            });
            return dataPoint;
          });
          
          // Colors for different schemes - vibrant color palette
          const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7B731', '#5F27CD', '#00D2D3'];
          
          return (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowSchemesModal(false);
              }}
            >
              <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 flex items-center justify-between z-10">
                  <div>
                    <h2 className="text-2xl font-bold">{amcName} - Schemes</h2>
                    <p className="text-purple-100 text-sm mt-1">Viewing {schemes.length} scheme(s)</p>
                  </div>
                  <button 
                    onClick={() => setShowSchemesModal(false)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Performance Comparison Bar Chart */}
                  <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Performance Comparison
                    </h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="period" 
                          tick={{ fill: '#475569', fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fill: '#475569', fontSize: 12 }}
                          label={{ value: 'Returns (%)', angle: -90, position: 'insideLeft', style: { fill: '#475569' } }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                          formatter={(value) => [`${value.toFixed(2)}%`, '']}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="circle"
                        />
                        {schemes.map((scheme, idx) => (
                          <Bar 
                            key={scheme.scheme_name}
                            dataKey={scheme.scheme_name} 
                            fill={colors[idx % colors.length]}
                            radius={[4, 4, 0, 0]}
                            name={scheme.scheme_name.length > 30 ? scheme.scheme_name.substring(0, 30) + '...' : scheme.scheme_name}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Performance Metrics Table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-slate-100 to-slate-50 px-4 py-3 border-b border-slate-200">
                      <h3 className="text-lg font-semibold text-slate-800">Performance Metrics (%)</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 sticky left-0 bg-slate-50">Scheme</th>
                            {periods.map((period) => (
                              <th key={period} className="px-4 py-3 text-center text-sm font-semibold text-slate-700 whitespace-nowrap">
                                {period}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {schemes.map((scheme, idx) => (
                            <tr key={scheme.scheme_name} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm sticky left-0 bg-white max-w-xs">
                                <div className="font-semibold text-slate-900">{scheme.amc_name || amcName}</div>
                                <div className="text-xs text-slate-600 mt-0.5">{scheme.scheme_name}</div>
                              </td>
                              {periodKeys.map((key) => (
                                <td 
                                  key={key}
                                  className={`px-4 py-3 text-center text-sm font-semibold whitespace-nowrap ${
                                    parseFloat(scheme[key]) >= 0 ? 'text-green-600' : 'text-red-600'
                                  }`}
                                >
                                  {parseFloat(scheme[key] || 0).toFixed(2)}%
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                {/* Scheme Details Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-100 to-pink-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Scheme Details</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 border-r border-slate-200 sticky left-0 bg-slate-50 w-48">
                            Field
                          </th>
                          {schemes.map((scheme, idx) => (
                            <th key={idx} className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                              <div className="text-xs font-normal text-slate-600 mt-1">{scheme.scheme_name}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fund Manager Name(s)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fund_manager || '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fund Manager Bio
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-xs text-slate-800 text-center max-w-xs">
                              {scheme.fund_manager_bio || '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Inception Date
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.inception_date || '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Edge in Investing
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-xs text-slate-800 text-center max-w-xs">
                              {scheme.edge_in_investing || '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 1 - Management Fee (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_1_management ? `${scheme.fee_structure_1_management}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 1 - Hurdle Rate (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_1_hurdle ? `${scheme.fee_structure_1_hurdle}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 1 - Performance Fee (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_1_performance ? `${scheme.fee_structure_1_performance}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 2 - Management Fee (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_2_management ? `${scheme.fee_structure_2_management}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 2 - Hurdle Rate (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_2_hurdle ? `${scheme.fee_structure_2_hurdle}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 2 - Performance Fee (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_2_performance ? `${scheme.fee_structure_2_performance}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 3 - Management Fee (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_3_management ? `${scheme.fee_structure_3_management}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 3 - Hurdle Rate (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_3_hurdle ? `${scheme.fee_structure_3_hurdle}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fee Structure 3 - Performance Fee (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_3_performance ? `${scheme.fee_structure_3_performance}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Exit Load (%)
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.exit_load ? `${scheme.exit_load}%` : '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Type of Fund
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fund_type || '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Size of Fund
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fund_size || '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Presentation Link
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3">
                              <div className="flex justify-center">
                                {scheme.presentation_link ? (
                                  <a 
                                    href={scheme.presentation_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-teal-600 hover:text-teal-700 text-sm flex items-center gap-1 font-medium"
                                  >
                                    View Link
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                ) : (
                                  '-'
                                )}
                              </div>
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Sales Contact Email
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.sales_email || '-'}
                            </td>
                          ))}
                        </tr>
                        <tr className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Sales Contact Mobile
                          </td>
                          {schemes.map((scheme, idx) => (
                            <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.sales_mobile || '-'}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default AMCDashboard;
